import type { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from 'vue-router';
import { defineComponent } from 'vue';
import {
  ADDON_CREATE_SUFFIX,
  ADDON_EDIT_SUFFIX,
  DISCOUNT_CREATE_SUFFIX,
  ORDER_DETAIL_SUFFIX,
  PRESALE_CREATE_SUFFIX,
  TICKET_CONVERT_SUFFIX,
  TICKET_COUNTS_REPORT_SUFFIX,
  TICKET_CREATE_SUFFIX,
  TICKET_EDIT_SUFFIX,
  TICKETING_TABS,
} from '@/router/ticketingTabs';
import { requireMemoEdit, requireMemoRead } from '@/router/requireMemoEdit';
import { requireWebhooksEdit, requireWebhooksRead } from '@/router/requireWebhooks';
import { redirectToSettlementTab } from '@/router/settlementTabRedirect';

const LEGACY_BASE_URL = import.meta.env.VITE_LEGACY_BASE_URL || null;

// Rails-rendered reports; every other `/reports/<slug>` is a Looker report.
const NATIVE_REPORT_TYPES = ['daily', 'payment', 'event_cancelation', 'email_opt_ins', 'email_opt_ins.csv'];

// Extract all lazy-loaded components from routes
const extractLazyComponents = (routes: RouteRecordRaw[]): (() => Promise<any>)[] => {
  const components: (() => Promise<any>)[] = [];

  const processRoute = (route: RouteRecordRaw) => {
    // Handle component property
    if (route.component && typeof route.component === 'function') {
      components.push(route.component as () => Promise<any>);
    }

    // Handle components property (named views)
    if (route.components) {
      Object.values(route.components).forEach(comp => {
        if (typeof comp === 'function') {
          components.push(comp as () => Promise<any>);
        }
      });
    }

    // Recursively process children
    if (route.children) {
      route.children.forEach(processRoute);
    }
  };

  routes.forEach(processRoute);
  return components;
};

// Preload all lazy-loaded components when the connection is idle
export const preloadComponents = () => {
  const requestIdleCallbackFn = window.requestIdleCallback || ((cb: IdleRequestCallback) => setTimeout(cb, 1));

  // Wait for the page to fully settle before preloading
  const startPreloading = () => {
    const allComponents = [
      ...extractLazyComponents(publicRoutes),
      ...extractLazyComponents(routes),
      ...extractLazyComponents(EventDetailsRoutes('Calendar')),
      ...extractLazyComponents(EventDetailsRoutes('Events')),
      ...extractLazyComponents(EventDetailsRoutes('Modal')),
      ...extractLazyComponents(DefaultSetRoutes('Venue')),
    ];

    // Preload each component with additional idle checks between batches
    let index = 0;
    const preloadNext = () => {
      if (index >= allComponents.length) {
        return;
      }

      requestIdleCallbackFn(() => {
        try {
          allComponents[index]();
        } catch {
          // Ignore errors during preloading
        }
        index++;
        preloadNext();
      });
    };

    preloadNext();
  };

  // Wait for load event + additional delay to ensure page has settled
  if (document.readyState === 'complete') {
    setTimeout(startPreloading, 500);
  } else {
    window.addEventListener('load', () => {
      setTimeout(startPreloading, 500);
    });
  }
};

// Initialize preloading if running in browser
if (typeof window !== 'undefined') {
  preloadComponents();
}

const NoopComponent = defineComponent({
  render: () => null,
});

/**
 * Where a legacy redirect leaves the router when it cannot hand the page back to where it came
 * from. `from.matched`, not `from.fullPath`: the initial location's `fullPath` is `'/'` — truthy,
 * and matched by nothing in the embedded hash router — so testing the path sent a deep link "back"
 * to a location that resolves to nothing. With no route to return to there is nowhere to go, so
 * abort and leave the page to the redirect that has already fired.
 *
 * Unreachable while the guards below hard-code `newTab` to `false` and return early. It is written
 * this way so that restoring the `target.origin !== window.location.origin` check they carry in a
 * comment cannot quietly reintroduce the defect.
 */
export const returnToOrigin = (from: RouteLocationNormalized) => (from.matched.length ? from : false);

export type EventDetailsContext = 'Calendar' | 'Events' | 'Modal';

/**
 * Event-details routes that must NOT carry the event side navigation.
 *
 * Everything in this tree is event-scoped, but only some of it is a *section*
 * of the event. The rest are leaf tasks you enter, complete and leave — showing
 * section navigation there invites you to wander off mid-task (sc-18486).
 *
 * Section views (navigation shown): Event.Edit, Event.Announce,
 * Event.ArtistIndex, Event.Artist.*, Event.Orders,
 * Event.Discounts, Event.Presales, Event.Order, Event.Discount.New,
 * Event.Presale.New, Event.Ticket.New, Event.Ticket.Edit, Event.Ticket.Convert, Event.Addon.New, Event.Addon.Edit (leaf tasks by the criterion above, but the design keeps the rail
 * on them, and a view may not render the rail itself). Event.Ticket.CountsReport is the report reached from the
 * Tickets tab and keeps the plain shell the report has always had.
 *
 * Routes outside this tree carry no `meta.context` and so resolve to the plain
 * shell on their own — see `useModalShell`. They need no entry here.
 */
const PLAIN_SHELL_ROUTE_SUFFIXES = [
  'Event.SeatingCharts',
  'Event.WillCallEmail',
  'Event.TicketCountsReport',
  TICKET_COUNTS_REPORT_SUFFIX,
  'Event.EmbedCode',
  'Event.New',
  'Hold.New',
];

// One closure shared by every ticketing route in every context. Each
// `() => import(…)` is a distinct function, and `extractLazyComponents` walks the
// factory's output once per context on top of the router's own calls, so an inline
// closure would have the preloader queue this chunk nine times over.
const ticketingModal = () => import('@/components/ticketing-modal/TicketingModal.vue');
const artistIndex = () => import('@/components/artist/ArtistIndex.vue');
const eventOrderDetail = () => import('@/components/ticketing-modal/EventOrderDetail.vue');
const eventDiscountCreate = () => import('@/components/ticketing-modal/EventDiscountCreate.vue');
const eventPresaleCreate = () => import('@/components/ticketing-modal/EventPresaleCreate.vue');
const eventTicketCreate = () => import('@/components/ticketing-modal/EventTicketCreate.vue');
const eventAddonCreate = () => import('@/components/ticketing-modal/EventAddonCreate.vue');
const eventTicketEdit = () => import('@/components/ticketing-modal/EventTicketEdit.vue');
const eventTicketConvert = () => import('@/components/ticketing-modal/EventTicketConvert.vue');
const ticketCountsReport = () => import('@/components/ticket-counts-report/TicketCountsReportModal.vue');
const eventAddonEdit = () => import('@/components/ticketing-modal/EventAddonEdit.vue');

export const EventDetailsRoutes = (context: EventDetailsContext, routePrefix: string = '', options = {}): RouteRecordRaw[] => {
  const componentName = options['componentName'] || 'SideDetail';
  // Every context goes back to its own artist index, embedded included. `Modal` used to be
  // special-cased to `{ path: '/modal' }` — the bare parent `router.ts` mounts this tree under,
  // which carries no component and so emptied the drawer instead of showing the list. The
  // embedded index is a real route (`/modal/events/:eventId/artists`) and is already what the
  // Rails page pushes for `showEventArtistsModal`.
  const backToArtistIndex = (eventId: string | string[]): RouteLocationRaw => ({ name: `${context}.Event.ArtistIndex`, params: { eventId } });
  return [
    {
      path: `${routePrefix}:eventId/seating-charts`,
      name: `${context}.Event.SeatingCharts`,
      components: {
        [componentName]: () => import('@/components/seating-charts/SeatingChartIndex.vue'),
      },
    },
    {
      path: `${routePrefix}:eventId/announce`,
      name: `${context}.Event.Announce`,
      components: {
        [componentName]: () => import('@/components/announce-modal/AnnounceModal.vue'),
      },
    },

    {
      path: `${routePrefix}:eventId/will-call/email`,
      name: `${context}.Event.WillCallEmail`,
      components: {
        [componentName]: () => import('@/components/will-call/WillCallEmailModal.vue'),
      },
    },

    {
      path: `${routePrefix}:eventId/ticket-count-report`,
      name: `${context}.Event.TicketCountsReport`,
      components: {
        [componentName]: ticketCountsReport,
      },
    },

    {
      path: `${routePrefix}:eventId/embed-code`,
      name: `${context}.Event.EmbedCode`,
      components: {
        [componentName]: () => import('@/components/embed-code/EmbedCodeModal.vue'),
      },
    },

    {
      path: `${routePrefix}:eventId/edit`,
      name: `${context}.Event.Edit`,
      components: {
        [componentName]: () => import('@/components/events-modal/VPEventEdit.vue'),
      },
    },

    {
      path: `${routePrefix}:eventId/artists`,
      name: `${context}.Event.ArtistIndex`,
      components: {
        [componentName]: artistIndex,
      },
    },

    {
      path: `${routePrefix}:eventId/artists/:artistId`,
      components: {
        [componentName]: () => import('@/components/artist/ArtistModal.vue'),
      },
      props: {
        // The back arrow's *default*, not where Close goes: Close dismisses the drawer, and the
        // arrow honours whatever origin the opener declared in history state. This is what it
        // falls back to when nobody declared one -- so an artist opened from the list still
        // returns to the list.
        [componentName]: route => ({
          backTo: backToArtistIndex(route.params.eventId),
        }),
      },
      children: [
        {
          path: '',
          name: `${context}.Event.Artist.Edit`,
          component: () => import('@/components/artist/ArtistInfoTab.vue'),
        },
        {
          path: 'riders/:riderType',
          name: `${context}.Event.Artist.Rider`,
          component: () => import('@/components/artist/ArtistRiderTab.vue'),
        },
        {
          path: 'settlements/:settlementId?',
          name: `${context}.Event.Artist.Settlement`,
          component: () => import('@/components/artist/ArtistSettlementTab.vue'),
        },
      ],
    },

    // Siblings rather than children of a `${routePrefix}:eventId` parent. In the
    // `Events` context a *matchable* parent there resolves to `/events/:eventId`, ties
    // with the legacy `EventDetails` record on matcher score, and wins on insertion
    // order — silently taking over the redirect out to the legacy event page. The
    // matcher map is keyed by name, so every `{ name: 'EventDetails' }` navigation
    // would keep working and only path navigation and reloads would break.
    // The `/events/<id>` assertion in `routes.spec.ts` guards it.
    ...TICKETING_TABS.map(({ segment, suffix }): RouteRecordRaw => ({
      path: `${routePrefix}:eventId/${segment}`,
      name: `${context}.${suffix}`,
      components: {
        [componentName]: ticketingModal,
      },
    })),

    // A sibling of the Orders tab rather than a child of it, for the same reason the tabs
    // are siblings: nothing in this tree gains a matchable parent. Three segments, so it
    // cannot tie with the two-segment tab record either way.
    {
      path: `${routePrefix}:eventId/orders/:orderId`,
      name: `${context}.${ORDER_DETAIL_SUFFIX}`,
      components: {
        [componentName]: eventOrderDetail,
      },
    },

    {
      path: `${routePrefix}:eventId/discounts/new`,
      name: `${context}.${DISCOUNT_CREATE_SUFFIX}`,
      components: {
        [componentName]: eventDiscountCreate,
      },
    },

    {
      path: `${routePrefix}:eventId/presales/new`,
      name: `${context}.${PRESALE_CREATE_SUFFIX}`,
      components: {
        [componentName]: eventPresaleCreate,
      },
    },

    {
      path: `${routePrefix}:eventId/tickets/new`,
      name: `${context}.${TICKET_CREATE_SUFFIX}`,
      components: {
        [componentName]: eventTicketCreate,
      },
    },

    {
      path: `${routePrefix}:eventId/addons/new`,
      name: `${context}.${ADDON_CREATE_SUFFIX}`,
      components: {
        [componentName]: eventAddonCreate,
      },
    },

    {
      path: `${routePrefix}:eventId/tickets/:ticketId(\\d+)/edit`,
      name: `${context}.${TICKET_EDIT_SUFFIX}`,
      components: {
        [componentName]: eventTicketEdit,
      },
    },

    {
      path: `${routePrefix}:eventId/tickets/holds/:holdId(\\d+)/convert`,
      name: `${context}.${TICKET_CONVERT_SUFFIX}`,
      components: {
        [componentName]: eventTicketConvert,
      },
    },

    {
      path: `${routePrefix}:eventId/tickets/ticket-counts`,
      name: `${context}.${TICKET_COUNTS_REPORT_SUFFIX}`,
      components: {
        [componentName]: ticketCountsReport,
      },
    },

    {
      path: `${routePrefix}:eventId/addons/:addonId(\\d+)/edit`,
      name: `${context}.${ADDON_EDIT_SUFFIX}`,
      components: {
        [componentName]: eventAddonEdit,
      },
    },

    {
      path: `${routePrefix}new`,
      name: `${context}.Event.New`,
      components: {
        [componentName]: () => import('@/components/events-modal/VPEventNew.vue'),
      },
    },
    {
      path: `${routePrefix}add-hold`,
      name: `${context}.Hold.New`,
      components: {
        [componentName]: () => import('@/components/events-modal/VPHoldNew.vue'),
      },
    },
    {
      path: `${routePrefix}:eventId/event-artists/:eventArtistId/settlements/:settlementId`,
      name: `${context}.ArtistSettlementSheet`,
      components: {
        [componentName]: artistIndex,
      },
      beforeEnter: redirectToSettlementTab(context),
    },
  ].map((route: RouteRecordRaw) => {
    const suffix = typeof route.name === 'string' ? route.name.slice(context.length + 1) : undefined;

    return {
      ...route,
      meta: {
        ...(route.meta || {}),
        context,
        ...(suffix && PLAIN_SHELL_ROUTE_SUFFIXES.includes(suffix) ? { shell: 'plain' as const } : {}),
      },
    };
  });
};

type DefaultSetContext = 'Venue';

export const DefaultSetRoutes = (context: DefaultSetContext, routePrefix: string = '', options = {}): RouteRecordRaw[] => {
  const componentName = options['componentName'] || 'SideDetail';
  return [
    {
      path: `${routePrefix}default-set`,
      name: `${context}.DefaultSet.Edit`,
      components: {
        [componentName]: () => import('@/components/default-set-modal/VPDefaultSetEdit.vue'),
      },
    },
  ];
};

export const publicRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/login/Login.vue'),
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/pages/password/ForgotPassword.vue'),
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/pages/password/ResetPassword.vue'),
  },
];

export const routes: RouteRecordRaw[] = [
  {
    path: '/admin/applications',
    name: 'Admin.Applications.Index',
    component: () => import('@/pages/admin/applications/AppConfigIndex.vue'),
  },
  {
    path: '/admin/applications/new',
    name: 'Admin.Applications.New',
    component: () => import('@/pages/admin/applications/AppConfigNew.vue'),
  },
  {
    path: '/admin/applications/:applicationId',
    component: () => import('@/pages/admin/applications/AppConfigSingle.vue'),
    children: [
      {
        path: '',
        name: 'Admin.Applications.Show',
        component: () => import('@/pages/admin/applications/AppConfigShow.vue'),
      },
      {
        path: 'edit',
        name: 'Admin.Applications.Edit',
        component: () => import('@/pages/admin/applications/AppConfigEdit.vue'),
      },
    ],
  },
  {
    path: '/admin/ticket-layouts',
    name: 'Admin.TicketLayouts.Index',
    component: () => import('@/pages/admin/ticket-layouts/TicketLayoutIndex.vue'),
  },
  {
    path: '/admin/ticket-layouts/new',
    name: 'Admin.TicketLayouts.New',
    component: () => import('@/pages/admin/ticket-layouts/TicketLayoutNew.vue'),
  },
  {
    path: '/admin/ticket-layouts/:layoutId',
    component: () => import('@/pages/admin/ticket-layouts/TicketLayoutSingle.vue'),
    children: [
      {
        path: 'edit',
        name: 'Admin.TicketLayouts.Edit',
        component: () => import('@/pages/admin/ticket-layouts/TicketLayoutEdit.vue'),
      },
    ],
  },
  {
    path: '/admin/orders',
    component: () => import ('@/pages/admin/orders/AdminOrderIndex.vue'),
    name: 'Admin.Orders.Index',
    children: [
      {
        path: ':orderId',
        name: 'Admin.Orders.Show',
        components: {
          SideDetail: () => import('@/pages/admin/orders/OrderDetails.vue'),
        },
      },
    ],
  },
  {
    path: '/settings/widgets',
    name: 'WidgetConfigIndex',
    component: () => import('@/components/widget/WidgetConfigIndex.vue'),
  },
  {
    path: '/settings/widgets/new',
    name: 'WidgetConfigNew',
    component: () => import('@/components/widget/WidgetConfigNew.vue'),
  },
  {
    path: '/settings/widgets/duplicate/:widgetId',
    name: 'WidgetConfigDuplicate',
    component: () => import('@/components/widget/WidgetConfigNew.vue'),
  },
  {
    path: '/settings/widgets/edit/:widgetId',
    name: 'WidgetConfigEdit',
    component: () => import('@/components/widget/WidgetConfigEdit.vue'),
  },

  {
    path: '/offer-sheets/:offerSheetsId',
    name: 'OfferSheet',
    components: {
      FullModal: () => import('@/components/offer-sheet/OfferSheet.vue'),
    },
  },
  {
    path: '/seating-charts/:eventId/new',
    name: 'SeatingCharts.New',
    components: {
      FullModal: () => import('@/components/seating-charts/SeatingChartDesigner.vue'),
    },
  },
  {
    path: '/seating-charts/:eventId/:chartKey/edit',
    name: 'SeatingCharts.Edit',
    components: {
      FullModal: () => import('@/components/seating-charts/SeatingChartDesigner.vue'),
    },
  },
  {
    path: '/settings/team',
    name: 'UserAccountIndex',
    component: () => import('@/pages/user-account/UserAccountIndex.vue'),
  },
  {
    path: '/settings/team/new',
    name: 'UserAccountNew',
    component: () => import('@/pages/user-account/UserAccountNew.vue'),
  },
  {
    path: '/settings/team/:userId/edit',
    name: 'UserAccountEdit',
    component: () => import('@/pages/user-account/UserAccountEdit.vue'),
  },
  {
    path: '/settings/venues',
    name: 'VenueIndex',
    component: () => import('@/pages/venues/VenueIndex.vue'),
  },
  {
    path: '/settings/venues/new',
    name: 'VenueNew',
    component: () => import('@/pages/venues/VenueNew.vue'),
  },
  {
    path: '/settings/venues/:venueId/edit',
    name: 'VenueEdit',
    component: () => import('@/pages/venues/VenueEdit.vue'),
    children: DefaultSetRoutes('Venue'),
  },
  {
    path: '/settings/team_roles',
    name: 'RoleIndex',
    component: () => import('@/pages/team-roles/RoleIndex.vue'),
  },
  {
    path: '/settings/team_roles/new',
    name: 'RoleNew',
    component: () => import('@/pages/team-roles/RoleNew.vue'),
  },
  {
    path: '/settings/team_roles/:roleId/edit',
    name: 'RoleEdit',
    component: () => import('@/pages/team-roles/RoleEdit.vue'),
  },
  {
    path: '/settings/users/:userId/edit',
    name: 'UserEdit',
    component: () => import('@/pages/user/UserEdit.vue'),
  },
  {
    path: '/settings/api-keys',
    name: 'ApiKeyIndex',
    component: () => import('@/pages/api-keys/ApiKeyIndex.vue'),
  },
  {
    path: '/settings/api-keys/new',
    name: 'ApiKeyNew',
    component: () => import('@/pages/api-keys/ApiKeyNew.vue'),
  },
  {
    path: '/settings/webhooks',
    name: 'WebhookSubscriptionIndex',
    component: () => import('@/pages/webhooks/WebhookSubscriptionIndex.vue'),
    beforeEnter: requireWebhooksRead,
  },
  {
    path: '/settings/webhooks/new',
    name: 'WebhookSubscriptionNew',
    component: () => import('@/pages/webhooks/WebhookSubscriptionNew.vue'),
    beforeEnter: requireWebhooksEdit,
  },
  {
    path: '/settings/webhooks/:subscriptionId/edit',
    name: 'WebhookSubscriptionEdit',
    component: () => import('@/pages/webhooks/WebhookSubscriptionEdit.vue'),
    beforeEnter: requireWebhooksEdit,
  },
  {
    path: '/settings/webhooks/:subscriptionId/deliveries',
    name: 'WebhookDeliveryHistory',
    component: () => import('@/pages/webhooks/WebhookDeliveryHistory.vue'),
    beforeEnter: requireWebhooksRead,
  },
  {
    path: '/settings/passkeys',
    name: 'PasskeyIndex',
    component: () => import('@/pages/passkeys/PasskeyIndex.vue'),
  },
  {
    path: '/whitelabel',
    name: 'WhitelabelIndex',
    component: () => import('@/pages/whitelabel/WhitelabelIndex.vue'),
  },
  {
    path: '/whitelabel/new',
    name: 'WhitelabelNew',
    component: () => import('@/pages/whitelabel/WhitelabelNew.vue'),
  },
  {
    path: '/whitelabel/:whitelabelId/edit',
    name: 'WhitelabelEdit',
    component: () => import('@/pages/whitelabel/WhitelabelEdit.vue'),
  },
  {
    path: '/user/change-password',
    name: 'ChangePassword',
    component: () => import('@/pages/user/ChangePassword.vue'),
  },
  {
    path: '/calendar',
    name: 'Calendar',
    component: () => import('@/pages/events/EventsCalendar.vue'),
    children: [
      ...EventDetailsRoutes('Calendar', 'events/'),
      {
        path: 'share-availability',
        name: 'Calendar.Availability.Share',
        components: {
          SideDetail: () => import('@/components/events-modal/VPShareAvailability.vue'),
        },
      },
      {
        path: 'memos/new',
        name: 'Calendar.Memo.New',
        components: {
          SideDetail: () => import('@/components/memos/VPMemoNew.vue'),
        },
        beforeEnter: requireMemoEdit,
      },
      {
        path: 'memos/:memoId',
        name: 'Calendar.Memo.Details',
        components: {
          SideDetail: () => import('@/components/memos/VPMemoDetails.vue'),
        },
        beforeEnter: requireMemoRead,
      },
    ],
  },

  {
    path: '/events',
    name: 'EventsIndex',
    component: () => import('@/pages/events/EventsIndex.vue'),
    children: EventDetailsRoutes('Events', ''),
  },

  {
    path: '/orders',
    name: 'Orders.Index',
    component: () => import('@/pages/orders/OrderIndex.vue'),
    children: [
      {
        path: ':orderId',
        name: 'Orders.Show',
        components: {
          SideDetail: () => import('@/pages/orders/OrderDetail.vue'),
        },
      },
    ],
  },

  {
    path: '/admin/accounts',
    name: 'Admin.Account.Index',
    component: () => import('@/pages/admin/accounts/AccountsIndex.vue'),
  },

  {
    path: '/admin/accounts/new',
    name: 'Admin.Account.New',
    component: () => import('@/pages/admin/accounts/AccountNew.vue'),
  },

  {
    path: '/admin/accounts/:accountId/edit',
    name: 'Admin.Account.Edit',
    component: () => import('@/pages/admin/accounts/AccountEdit.vue'),
    children: [
      {
        path: 'history',
        name: 'Admin.Account.History',
        components: {
          SideDetail: () => import('@/pages/admin/features/FeatureFlagHistory.vue'),
        },
        props: {
          SideDetail: route => ({
            accountId: Number(route.params.accountId),
            backTo: { name: 'Admin.Account.Edit', params: { accountId: route.params.accountId } },
          }),
        },
      },
    ],
  },

  // Claims `/admin/users` from the `Admin.Legacy` catch-all below, which until now forwarded it to
  // Rails only so the nav entry led somewhere. A static segment outranks `:page` in vue-router, so
  // this record wins regardless of where it sits in the array.
  {
    path: '/admin/users',
    name: 'Admin.Users.Index',
    component: () => import('@/pages/admin/users/UsersIndex.vue'),
  },

  {
    path: '/admin/account_features',
    name: 'Admin.Features.Index',
    component: () => import('@/pages/admin/features/FeaturesIndex.vue'),
    children: [
      // The client drill-down, shaped like `Admin.EmailDeliverability.Group`: the inventory stays
      // mounted and the panel renders into its `SideDetail` named view. The flag's machine name is
      // the only thing in the path; the panel's own search and holding filter stay in the query, so
      // a copied link carries the list the operator was looking at.
      {
        path: ':feature',
        name: 'Admin.Features.Show',
        components: {
          SideDetail: () => import('@/pages/admin/features/FeatureAccountsPanel.vue'),
        },
      },
      // Its own drawer in the same `SideDetail` view, so the clients and the audit log never show together.
      {
        path: ':feature/history',
        name: 'Admin.Features.History',
        components: {
          SideDetail: () => import('@/pages/admin/features/FeatureFlagHistory.vue'),
        },
        props: {
          SideDetail: route => ({ feature: String(route.params.feature), backTo: { name: 'Admin.Features.Index' } }),
        },
      },
    ],
  },

  // No `meta` and no guard: this app has no staff route guard, and inventing one for a single
  // screen would be a new mechanism rather than a convention. The page is gated by the nav entry
  // sitting under `PermissionKey.AdminTools`, by the internal endpoint 401ing before the query is
  // parsed, and by the screen's own staff-only branch.
  {
    path: '/admin/email-deliverability',
    name: 'Admin.EmailDeliverability.Index',
    component: () => import('@/pages/admin/email-deliverability/EmailDeliverabilityIndex.vue'),
    children: [
      // The drill-down panel, shaped like `Admin.Orders.Show` above: the index stays mounted and the
      // child renders into its `SideDetail` named view, which is what keeps the summary on screen
      // beside the deliveries behind one of its rows. No `meta`, so `useModalShell` resolves the
      // plain shell — this panel has no event navigation to host.
      //
      // Only the row's key is in the path. The dimension it belongs to already travels in the
      // summary's own `?groupBy=`, so one route serves all three groupings and a shared link carries
      // the grouping it was copied from without a second path segment to keep in step with it.
      {
        path: ':groupKey',
        name: 'Admin.EmailDeliverability.Group',
        components: {
          SideDetail: () => import('@/pages/admin/email-deliverability/EmailDeliveryGroupDetails.vue'),
        },
      },
    ],
  },

  // Dev harness for the internal (VP staff) GraphQL connection (ADR-009 step 0).
  // Unlinked from any nav -- reachable only by typing the route.
  {
    path: '/dev/internal-graphql',
    name: 'Dev.InternalGraphql',
    component: () => import('@/pages/dev/InternalGraphqlSmoke.vue'),
  },

  {
    path: '/design-system',
    component: () => import('@/pages/design-system/DesignSystemIndex.vue'),
    children: [
      { path: '', redirect: { name: 'DesignSystem.Buttons' } },
      {
        path: 'buttons',
        name: 'DesignSystem.Buttons',
        component: () => import('@/pages/design-system/DesignSystemButtons.vue'),
      },
      {
        path: 'filters',
        name: 'DesignSystem.Filters',
        component: () => import('@/pages/design-system/DesignSystemFilters.vue'),
      },
      {
        path: 'quantity-input',
        name: 'DesignSystem.QuantityInput',
        component: () => import('@/pages/design-system/DesignSystemQuantityInput.vue'),
      },
      {
        path: 'datetime-input',
        name: 'DesignSystem.DateTimeInput',
        component: () => import('@/pages/design-system/DesignSystemDateTimeInput.vue'),
      },
      {
        path: 'chips',
        name: 'DesignSystem.Chips',
        component: () => import('@/pages/design-system/DesignSystemChips.vue'),
      },
      {
        path: 'modal-header',
        name: 'DesignSystem.ModalHeader',
        component: () => import('@/pages/design-system/DesignSystemModalHeader.vue'),
      },
      {
        path: 'modal-footer',
        name: 'DesignSystem.ModalFooter',
        component: () => import('@/pages/design-system/DesignSystemModalFooter.vue'),
      },
      {
        path: 'detail-section-row',
        name: 'DesignSystem.DetailSectionRow',
        component: () => import('@/pages/design-system/DesignSystemDetailSectionRow.vue'),
      },
      {
        path: 'radio-button',
        name: 'DesignSystem.RadioButton',
        component: () => import('@/pages/design-system/DesignSystemRadioButton.vue'),
      },
      {
        path: 'toggle',
        name: 'DesignSystem.Toggle',
        component: () => import('@/pages/design-system/DesignSystemToggle.vue'),
      },
      {
        path: 'confirmation-dialog',
        name: 'DesignSystem.ConfirmationDialog',
        component: () => import('@/pages/design-system/DesignSystemConfirmationDialog.vue'),
      },
      {
        path: 'text-input-dialog',
        name: 'DesignSystem.TextInputDialog',
        component: () => import('@/pages/design-system/DesignSystemTextInputDialog.vue'),
      },
    ],
  },

  // legacy routes
  {
    path: '/events/:eventId',
    name: 'EventDetails',
    component: NoopComponent,
    beforeEnter: (to, from) => {
      const target = new URL(`/events/${to.params.eventId}`, LEGACY_BASE_URL || window.location.origin);
      const newTab = false; // target.origin !== window.location.origin;

      window.open(target, newTab ? '_blank' : '_self');

      if (!newTab) {
        return false;
      }

      return returnToOrigin(from);
    },
  },

  {
    path: '/event-groups/:eventGroupId',
    name: 'EventGroupDetails',
    component: NoopComponent,
    beforeEnter: (to, from) => {
      const target = new URL(`/venue_app/event_groups/${to.params.eventGroupId}/edit`, LEGACY_BASE_URL || window.location.origin);
      const newTab = false; // target.origin !== window.location.origin;

      window.open(target, newTab ? '_blank' : '_self');

      if (!newTab) {
        return false;
      }

      return returnToOrigin(from);
    },
  },

  {
    path: '/calendar/:date/closeout-sheet',
    name: 'CloseoutSheet',
    component: NoopComponent,
    beforeEnter: to => {
      const target = new URL(`/venue_app/calendar/${to.params.date}/closeout_sheet`, LEGACY_BASE_URL || window.location.origin);
      window.open(target, '_self');
    },
  },

  {
    path: `/reports/:type(${NATIVE_REPORT_TYPES.map(type => type.replace(/\./g, '\\.')).join('|')})`,
    name: 'Reports',
    component: NoopComponent,
    beforeEnter: to => {
      const target = new URL(`/reports/${to.params.type}`, LEGACY_BASE_URL || window.location.origin);
      window.open(target, '_self');
    },
  },
  {
    path: '/reports/:slug',
    name: 'LookerReport',
    component: () => import('@/pages/looker/LookerReport.vue'),
  },
  {
    path: '/settings/integrations',
    name: 'Settings.Integrations',
    component: NoopComponent,
    beforeEnter: () => {
      const target = new URL('/settings/integrations', LEGACY_BASE_URL || window.location.origin);
      window.open(target, '_self');
    },
  },
  {
    path: '/settings/organizational_discounts',
    name: 'Settings.OrganizationalDiscounts',
    component: NoopComponent,
    beforeEnter: () => {
      const target = new URL('/settings/organizational_discounts', LEGACY_BASE_URL || window.location.origin);
      window.open(target, '_self');
    },
  },

  {
    path: '/admin/deals',
    name: 'Admin.Deals.Legacy',
    component: NoopComponent,
    beforeEnter: () => {
      const target = new URL('/venue_app/deals', LEGACY_BASE_URL || window.location.origin);
      window.open(target, '_self');
    },
  },
  {
    path: '/admin/genres',
    component: () => import('@/pages/admin/genres/GenresIndex.vue'),
    children: [
      {
        path: '',
        name: 'Admin.Genres',
        component: () => import('@/pages/admin/genres/GenresTypeTab.vue'),
      },
      {
        path: 'category/:categoryId/subcategory',
        name: 'Admin.Genres.Category.Subcategory',
        component: () => import('@/pages/admin/genres/GenresSubcategoryTab.vue'),
      },
      {
        path: 'category/:categoryId?',
        name: 'Admin.Genres.Category',
        component: () => import('@/pages/admin/genres/GenresCategoryTab.vue'),
      },
    ],
  },
  {
    path: '/admin/:page',
    name: 'Admin.Legacy',
    component: NoopComponent,
    beforeEnter: to => {
      const target = new URL(`/admin/${to.params.page}`, LEGACY_BASE_URL || window.location.origin);
      window.open(target, '_self');
    },
  },
  {
    path: '/settings/users/:userId/edit',
    name: 'UserAccountEditLegacy',
    component: NoopComponent,
    beforeEnter: to => {
      const target = new URL(`/settings/users/${to.params.userId}/edit`, LEGACY_BASE_URL || window.location.origin);
      window.open(target, '_self');
    },
  },
];
